import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Grid,
    IconButton,
    Button,
    Typography,
    Avatar,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Tooltip
} from '@mui/material';
import { ReplyOutlinedIcon, ArrowBackIosNewOutlinedIcon, KeyboardArrowDownOutlinedIcon, MoreHorizOutlinedIcon, VisibilityOutlinedIcon, ShareOutlinedIcon, ContentCopyOutlinedIcon, QrCodeOutlinedIcon, LockResetOutlinedIcon, DeleteOutlineIcon, AddIcon, SettingsOutlinedIcon, PersonAddAlt1OutlinedIcon, EmailOutlinedIcon, PersonOutlineOutlinedIcon } from 'icons/materialIcons';
import { getUserById } from 'apis/authApi';
import { getCardById, duplicateCard, deleteCard } from 'apis/cardApi';
import TeamDarkButtonWithIcon from 'team/shared/components/button/TeamDarkButtonWithIcon';
import TeamCardListModal from './TeamCardListModal';
import TeamUpgradeSnackBar from 'team/shared/components/notification/TeamUpgradeSnackBar';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import userAvatar from 'images/avatar-cropped.png';
import { v4 as uuidv4 } from 'uuid'; // Import UUID library
import TeamShareYourCardModal from 'team/shared/features/card/TeamShareYourCardModal';
import TeamMemberSettingsModal from './TeamMemberSettingsModal';
import TeamDuplicateMemberModal from './TeamDuplicateMemberModal';
import TeamRemoveMemberModal from './TeamRemoveMemberModal';
import TeamDeleteMemberModal from './TeamDeleteMemberModal';
import QRCodeStyling from 'qr-code-styling';
import { FRONTEND_BASE_URL } from 'utils/apiUtils';
import { useQRCodeContext } from 'contexts/QrCodeContext'; // Import QRCode context
import { useAuth } from 'contexts/AuthContext';
import { forgotPassword } from 'apis/authApi';
import { useCardContext } from 'contexts/CardContext';
import TeamDeleteCardModal from './TeamDeleteCardModal';
import TeamSubteamListModal from './TeamSubteamListModal';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { useTeam } from 'team/contexts/teamContext';
import { getTeamMembers } from 'team/apis/teamMemberApi';
import { getSubteamColor, getSubteamInitialsWithDuplicates } from 'team/shared/utils/subteamUtils';

const TeamEditMemberNavbar = ({ user }) => {
    const navigate = useNavigate();
    const [userData, setUserData] = useState(null);
    const [userName, setUserName] = useState(null);
    const [userEmail, setUserEmail] = useState(null);
    const { cardId } = useParams();
    const [cardDetails, setCardDetails] = useState({});
    const [cardName, setCardName] = useState('');
    const [cardFullName, setCardFullName] = useState('');
    const [cardProfilePictureUrl, setCardProfilePictureUrl] = useState('');
    const [loading, setLoading] = useState(true);
    const [cardEmail, setCardEmail] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const buttonRef = useRef(null);
    const tempCardId = uuidv4();
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [resettingPassword, setResettingPassword] = useState(false);
    const qrDownloadRef = useRef(null);
    const { authState } = useAuth();
    const { updateCardData } = useCardContext();
    const { currentTeamId } = useTeam();

    // Menu state for three dots button
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const menuOpen = Boolean(menuAnchorEl);
    const [isDeleteCardModalOpen, setIsDeleteCardModalOpen] = useState(false);
    const [isNewCardModalOpen, setIsNewCardModalOpen] = useState(false);
    const [isMemberSettingsModalOpen, setIsMemberSettingsModalOpen] = useState(false);
    const [isDuplicateMemberModalOpen, setIsDuplicateMemberModalOpen] = useState(false);
    const [isRemoveMemberModalOpen, setIsRemoveMemberModalOpen] = useState(false);
    const [isDeleteMemberModalOpen, setIsDeleteMemberModalOpen] = useState(false);
    const [isSubteamModalOpen, setIsSubteamModalOpen] = useState(false);
    const subteamButtonRef = useRef(null);
    const [memberSubteams, setMemberSubteams] = useState([]);
    const [loadingSubteams, setLoadingSubteams] = useState(false);
    const isCompanyPage = Boolean(user?.isCompanyPage || userData?.isCompanyPage);
    const displayMemberName = isCompanyPage
        ? (cardFullName || cardName || 'No Name')
        : (cardFullName || cardName || 'Select Card');

    // Notification state
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Get QR Code context
    const { qrCodeState, initializeQRCode } = useQRCodeContext();

    const handleMenuOpen = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    // Card modal handlers
    const handleCardDropdownOpen = (event) => {
        setIsNewCardModalOpen(true);
    };

    const handleCardDropdownClose = () => {
        setIsNewCardModalOpen(false);
    };

    const handleSubteamDropdownOpen = (event) => {
        setIsSubteamModalOpen(true);
    };

    const handleSubteamDropdownClose = () => {
        setIsSubteamModalOpen(false);
    };

    const handleNotificationClose = () => {
        setNotification({ ...notification, open: false });
    };

    const handleShareCard = () => {
        setShareModalOpen(true);
        handleMenuClose();
    };

    const handleMemberSettings = () => {
        setIsMemberSettingsModalOpen(true);
        handleMenuClose();
    };

    const handleMemberSettingsModalClose = () => {
        setIsMemberSettingsModalOpen(false);
    };

    const handleDuplicateMember = () => {
        setIsDuplicateMemberModalOpen(true);
        handleMenuClose();
    };

    const handleDuplicateMemberModalClose = () => {
        setIsDuplicateMemberModalOpen(false);
    };

    const handleEditEmail = () => {
        // TODO: Implement edit email functionality
        console.log('Editing email...');
        handleMenuClose();
    };

    const handleResendInvite = async () => {
        try {
            handleMenuClose();

            setNotification({
                open: true,
                message: 'Resending invitation...',
                severity: 'info'
            });

            // Import team member API
            const { default: teamMemberApi } = await import('team/apis/teamMemberApi');
            const teamId = cardDetails?.card?.teamId || user?.teamId;

            const response = await teamMemberApi.resendInvitation(teamId, user?.userId);

            if (response.success) {
                setNotification({
                    open: true,
                    message: 'Invitation email resent successfully',
                    severity: 'success'
                });
            } else {
                throw new Error(response.message || 'Failed to resend invitation');
            }
        } catch (error) {
            console.error('Error resending invitation:', error);
            setNotification({
                open: true,
                message: error.response?.data?.message || 'Failed to resend invitation. This feature only works for pending invitations.',
                severity: 'error'
            });
        }
    };

    const handleRemoveMember = () => {
        handleMenuClose();
        setIsRemoveMemberModalOpen(true);
    };

    const handleRemoveMemberClose = () => {
        setIsRemoveMemberModalOpen(false);
    };

    const handleRemoveMemberConfirm = async () => {
        try {
            setNotification({
                open: true,
                message: 'Removing member from team...',
                severity: 'info'
            });
            setIsRemoveMemberModalOpen(false);

            const { default: teamApi } = await import('team/apis/teamApi');
            const teamId = cardDetails?.card?.teamId || user?.teamId;
            const response = await teamApi.removeTeamMember(teamId, user?.userId);

            if (response.success) {
                setNotification({
                    open: true,
                    message: 'Member removed from team successfully',
                    severity: 'success'
                });

                // Navigate to members list after short delay
                setTimeout(() => {
                    navigate('/team/members');
                }, 1500);
            }
        } catch (error) {
            console.error('Error removing member:', error);
            setNotification({
                open: true,
                message: error.response?.data?.message || 'Failed to remove member from team',
                severity: 'error'
            });
        }
    };

    const handleDeleteMember = () => {
        handleMenuClose();
        setIsDeleteMemberModalOpen(true);
    };

    const handleDeleteMemberClose = () => {
        setIsDeleteMemberModalOpen(false);
    };

    const handleDeleteMemberConfirm = async () => {
        try {
            setNotification({
                open: true,
                message: 'Deleting member...',
                severity: 'info'
            });
            setIsDeleteMemberModalOpen(false);

            const { default: teamApi } = await import('team/apis/teamApi');
            const teamId = cardDetails?.card?.teamId || user?.teamId;
            const response = await teamApi.deleteTeamMember(teamId, user?.userId);

            if (response.success) {
                setNotification({
                    open: true,
                    message: 'Member deleted successfully',
                    severity: 'success'
                });

                // Navigate to members list after short delay
                setTimeout(() => {
                    navigate('/team/members');
                }, 1500);
            }
        } catch (error) {
            console.error('Error deleting member:', error);
            setNotification({
                open: true,
                message: error.response?.data?.message || 'Failed to delete member',
                severity: 'error'
            });
        }
    };

    const handleViewCard = () => {
        // Open the card in a new tab
        window.open(`/card/${cardId}`, '_blank');
        handleMenuClose();
    };

    const handleDuplicateCard = async () => {
        try {
            setNotification({
                open: true,
                message: 'Duplicating card...',
                severity: 'info'
            });
            handleMenuClose();

            const response = await duplicateCard(cardId, user.userId);

            if (response.success) {
                setNotification({
                    open: true,
                    message: 'Card duplicated successfully',
                    severity: 'success'
                });

                // Navigate to team members page
                // Navigate with state containing the notification
                navigate('/team/members', {
                    state: {
                        notification: {
                            message: 'Card duplicated successfully',
                            severity: 'success'
                        }
                    }
                });
            }
        } catch (error) {
            console.error('Error duplicating card:', error);
            setNotification({
                open: true,
                message: error.response?.data?.message || 'Failed to duplicate card',
                severity: 'error'
            });
        }
    };

    const handleDeleteCardClick = () => {
        handleMenuClose();
        setIsDeleteCardModalOpen(true);
    };

    const handleDeleteCardConfirm = async () => {
        try {
            setNotification({
                open: true,
                message: 'Deleting card...',
                severity: 'info'
            });
            setIsDeleteCardModalOpen(false);

            const response = await deleteCard(cardId, user.userId);

            if (response.success) {
                setNotification({
                    open: true,
                    message: 'Card deleted successfully',
                    severity: 'success'
                });

                // Navigate back to team members page
                navigate('/team/members');
            }
        } catch (error) {
            console.error('Error deleting card:', error);
            setNotification({
                open: true,
                message: error.message || 'Failed to delete card',
                severity: 'error'
            });
        }
    };

    const handleDeleteCardCancel = () => {
        setIsDeleteCardModalOpen(false);
    };

    // Initialize QR Code for download
    useEffect(() => {
        if (cardId) {
            initializeQRCode(cardId);

            // Generate the card URL for QR code
            const BASE_URL = `${FRONTEND_BASE_URL}/card`;
            const cardUrl = `${BASE_URL}/${cardId}?source=qr_code&ts=${Date.now()}`;

            // Initialize the QR code generator for downloading
            const contextQrCodeColor = qrCodeState[cardId]?.contextQrCodeColor || '#000000';
            const contextQrCodeLogoUrl = qrCodeState[cardId]?.contextQrCodeLogoUrl || '';

            qrDownloadRef.current = new QRCodeStyling({
                width: 1000,
                height: 1000,
                type: "png",
                data: cardUrl,
                image: contextQrCodeLogoUrl,
                dotsOptions: {
                    color: contextQrCodeColor,
                    type: "square"
                },
                cornersSquareOptions: {
                    type: "square"
                },
                cornersDotOptions: {
                    type: "square"
                },
                backgroundOptions: {
                    color: "#ffffff"
                },
                imageOptions: {
                    crossOrigin: "anonymous",
                    margin: 7,
                    imageSize: 0.4,
                    hideBackgroundDots: true,
                },
                qrOptions: {
                    errorCorrectionLevel: "H"
                },
            });
        }
    }, [cardId, initializeQRCode, qrCodeState]);

    const handleDownloadQRCode = async () => {
        if (!qrDownloadRef.current || downloading) return;

        try {
            setDownloading(true);
            handleMenuClose();

            const fileName = `qr-code-${cardId || 'card'}`;
            await qrDownloadRef.current.download({
                extension: 'png',
                name: fileName,
            });
        } catch (error) {
            console.error('Error downloading QR code:', error);
            setNotification({
                open: true,
                message: 'Error downloading QR code. Please try again.',
                severity: 'error'
            });
        } finally {
            setDownloading(false);
        }
    };

    const handleResetPassword = async () => {
        // Use the email from card or from the logged-in user
        const userEmail = cardEmail || authState?.user?.email;

        if (!userEmail) {
            setNotification({
                open: true,
                message: 'Unable to determine email address for password reset.',
                severity: 'error'
            });
            handleMenuClose();
            return;
        }

        try {
            setResettingPassword(true);
            handleMenuClose();

            const response = await forgotPassword(userEmail);

            if (response.success) {
                setNotification({
                    open: true,
                    message: 'Password reset email sent successfully.',
                    severity: 'success'
                });
            } else {
                throw new Error(response.message || 'Failed to send reset email');
            }
        } catch (error) {
            console.error('Error sending password reset:', error);
            setNotification({
                open: true,
                message: error.message || 'Failed to send password reset email.',
                severity: 'error'
            });
        } finally {
            setResettingPassword(false);
        }
    };

    const handleBackClick = () => {
        navigate(-1);
    };

    useEffect(() => {
        const fetchCardDetails = async () => {
            try {
                console.log('Fetching card details...');
                const data = await getCardById(cardId);
                setCardDetails(data);
                setLoading(false);
                console.log('Card Details:', data);
            } catch (error) {
                console.error('Error fetching card details:', error);
                setLoading(false);
            }
        };

        // ✅ FIX: Skip backend call for new card creation (UUID)
        const isNewCardCreation = cardId && cardId.includes('-') && cardId.length >= 32 && cardId.length <= 36;

        if (cardId && !isNewCardCreation) {
            fetchCardDetails();
        } else if (isNewCardCreation) {
            console.log('🔍 EditCardNavbar: New card creation - skipping card details fetch');
            setLoading(false);
        }
    }, [cardId]);

    // Fetch member subteams
    useEffect(() => {
        const fetchMemberSubteams = async () => {
            if (!user?.userId) return;
            
            const teamId = cardDetails?.card?.teamId || currentTeamId;
            if (!teamId) return;

            setLoadingSubteams(true);
            try {
                const response = await getTeamMembers(teamId, { status: 'active' });
                
                if (response && response.members) {
                    const member = response.members.find(m => {
                        const memberUserId = m.userId || m.user?.userId;
                        return memberUserId === user.userId;
                    });

                    if (member && member.subteamIds && Array.isArray(member.subteamIds)) {
                        const subteams = member.subteamIds.map(subteam => {
                            if (subteam && typeof subteam === 'object' && subteam._id) {
                                return {
                                    _id: subteam._id.toString(),
                                    name: subteam.name || 'Unnamed Subteam',
                                    logoUrl: subteam.logoUrl || '',
                                    description: subteam.description || ''
                                };
                            }
                            return {
                                _id: subteam.toString(),
                                name: 'Subteam',
                                logoUrl: '',
                                description: ''
                            };
                        });
                        setMemberSubteams(subteams);
                    } else {
                        setMemberSubteams([]);
                    }
                }
            } catch (error) {
                console.error('Error fetching member subteams:', error);
                setMemberSubteams([]);
            } finally {
                setLoadingSubteams(false);
            }
        };

        fetchMemberSubteams();
    }, [user?.userId, cardDetails?.card?.teamId, currentTeamId]);

    useEffect(() => {
        if (cardDetails?.card) {
            console.log('🔍 Setting card details:', {
                cardName: cardDetails.card.cardName,
                fullName: cardDetails.card.contact_info?.fullName,
                email: cardDetails.card.contact_info?.email,
                profilePictureUrl: cardDetails.card.profilePictureUrl
            });
            setCardFullName(cardDetails.card.contact_info?.fullName || '');
            setCardProfilePictureUrl(cardDetails.card.profilePictureUrl || '');
            setCardName(cardDetails.card.cardName || '');
            setCardEmail(cardDetails.card.contact_info?.email || '');
        }
    }, [cardDetails]);

    useEffect(() => {
        console.log("card profile photo++", cardProfilePictureUrl);
    }, []);

    // Debug cardName state changes
    useEffect(() => {
        console.log('🔍 cardName state changed:', cardName);
    }, [cardName]);

    // Debug cardFullName state changes
    useEffect(() => {
        console.log('🔍 cardFullName state changed:', cardFullName);
    }, [cardFullName]);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const idToken = localStorage.getItem('authToken');
                if (!idToken) {
                    throw new Error('No auth token found');
                }
                const response = await getUserById(user.userId, idToken);
                setUserData(response.data);
                console.log('Fetched user data from EditCardNavbar:', response.data.fullName);
            } catch (error) {
                console.error('Error fetching user data:', error.message);
            }
        };

        fetchUserData();
    }, [user.userId]);

    // Modified handleCreateNewCard function
    const handleCreateNewCard = () => {
        // Navigate to create card for myself in team context
        navigate(`/team/members/card/add-card/${tempCardId}?action=create`);
        window.location.reload();
    };

    const handleCardTypeSelect = (type) => {
        if (type === 'team-member') {
            setSnackbarOpen(true);
        } else if (type === 'myself') {
            navigate(`/team/members/card/add-card/${tempCardId}?action=create`);
            window.location.reload();
        }
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const handleSubscribe = () => {
        setSnackbarOpen(false);
        navigate('/dashboard/subscribe');
    };


    // Render subteam avatars for button
    const renderSubteamButtonIcon = () => {
        if (loadingSubteams) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupOutlinedIcon sx={{ fontSize: '1rem', color: '#828282' }} />
                    <Box
                        sx={{
                            backgroundColor: '#f7f7f7',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <AddIcon
                            sx={{
                                fontSize: '0.875rem',
                                color: '#828282'
                            }}
                        />
                    </Box>
                </Box>
            );
        }

        if (memberSubteams.length === 0) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupOutlinedIcon sx={{ fontSize: '1rem', color: '#828282' }} />
                    <Box
                        sx={{
                            backgroundColor: '#f7f7f7',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <AddIcon
                            sx={{
                                fontSize: '0.875rem',
                                color: '#828282'
                            }}
                        />
                    </Box>
                </Box>
            );
        }

        // Generate initials with duplicate handling
        const subteamsWithInitials = getSubteamInitialsWithDuplicates(memberSubteams);

        // Show overlapping circles (max 3 visible, rest as +N) + plus icon
        const maxVisible = 3;
        const visibleSubteams = subteamsWithInitials.slice(0, maxVisible);
        const remainingCount = subteamsWithInitials.length - maxVisible;

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                {visibleSubteams.map((subteam, index) => (
                    <Tooltip
                        key={subteam._id}
                        title={subteam.name}
                        placement="top"
                        arrow
                    >
                        <Avatar
                            src={subteam.logoUrl}
                            sx={{
                                width: 25,
                                height: 25,
                                position: 'relative',
                                zIndex: visibleSubteams.length - index,
                                marginLeft: index > 0 ? '-10px' : 0,
                                border: '2px solid #ffffff',
                                backgroundColor: subteam.logoUrl ? 'transparent' : getSubteamColor(subteam._id, subteam.name),
                                flexShrink: 0,
                                fontSize: (subteam.displayInitials?.length || 0) > 1 ? '0.65rem' : '0.75rem'
                            }}
                        >
                            {!subteam.logoUrl && subteam.displayInitials}
                        </Avatar>
                    </Tooltip>
                ))}
                {remainingCount > 0 && (
                    <Tooltip
                        title={`${remainingCount} more subteam${remainingCount > 1 ? 's' : ''}`}
                        placement="top"
                        arrow
                    >
                        <Avatar
                            sx={{
                                width: 25,
                                height: 25,
                                position: 'relative',
                                zIndex: 0,
                                marginLeft: '-10px',
                                border: '2px solid #ffffff',
                                backgroundColor: '#e0e0e0',
                                color: '#666',
                                fontSize: '0.7rem',
                                flexShrink: 0
                            }}
                        >
                            +{remainingCount}
                        </Avatar>
                    </Tooltip>
                )}
                {/* Plus icon in circular box - always shown */}
                <Box
                    sx={{
                        backgroundColor: '#f7f7f7',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginLeft: visibleSubteams.length > 0 ? '8px' : '4px'
                    }}
                >
                    <AddIcon
                        sx={{
                            fontSize: '0.875rem',
                            color: '#828282'
                        }}
                    />
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ padding: 2 }}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm="auto">
                    <Grid container alignItems="center" spacing={1}>
                        <Grid item>
                            <IconButton
                                sx={{
                                    backgroundColor: 'transparent',
                                    color: '#828282',
                                    padding: 0,
                                }}
                                onClick={handleBackClick}
                            >
                                <ArrowBackIosNewOutlinedIcon />
                            </IconButton>
                        </Grid>
                        <Grid item>
                            <Box
                                sx={{
                                    height: 25,
                                    width: '1px',
                                    backgroundColor: '#e0e0e0',
                                }}
                            />
                        </Grid>
                        <Grid item>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                    src={cardProfilePictureUrl || userAvatar}
                                    alt="User Avatar"
                                    sx={{
                                        width: 50,
                                        height: 50,
                                        border: '2px solid #ffffff',
                                    }}
                                />
                                <Box sx={{ marginLeft: 2 }}>
                                    <Typography variant="body2" sx={{ fontSize: '1.2rem', fontWeight: 600, color: '#333' }}>
                                        {displayMemberName}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#828282', fontSize: '0.9rem', fontWeight: 500 }}>
                                        {cardEmail}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12} sm container justifyContent="flex-end" spacing={2}>
                    <Grid item xs={12} sm="auto">
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                ref={buttonRef}
                                variant="contained"
                                sx={{
                                    backgroundColor: '#fff',
                                    color: '#e0e0e0',
                                    fontWeight: '500',
                                    borderRadius: '8px',
                                    border: '1px #e0e0e0 solid',
                                    textTransform: 'none',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    alignSelf: 'center',
                                    width: '200px',
                                    padding: '6.5px 16px',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#fff',
                                        color: '#e0e0e0',
                                        boxShadow: 'none',
                                    },
                                    '& .MuiButton-startIcon': {
                                        marginRight: 1,
                                    },
                                    '& .MuiButton-endIcon': {
                                        marginLeft: 1,
                                    },
                                }}
                                startIcon={
                                    <Avatar
                                        src={cardProfilePictureUrl || userAvatar}
                                        alt="User Avatar"
                                        sx={{
                                            width: 25,
                                            height: 25,
                                            border: '2px solid #ffffff',
                                        }}
                                    />
                                }
                                endIcon={<KeyboardArrowDownOutlinedIcon />}
                                onClick={handleCardDropdownOpen}
                            >
                                <Typography sx={{ color: '#000', flexGrow: 1, textAlign: 'left', fontSize: "0.9rem", marginLeft: "10px" }}>
                                    {displayMemberName}
                                </Typography>
                            </Button>

                            {/* Card List Modal */}
                            <TeamCardListModal
                                isOpen={isNewCardModalOpen}
                                onClose={handleCardDropdownClose}
                                onCreate={handleCreateNewCard}
                                anchorEl={buttonRef.current}
                                user={user}
                                cardId={cardId}
                                navigate={navigate}
                            />
                        </Box>
                    </Grid>

                    <Grid item xs={12} sm="auto">
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                ref={subteamButtonRef}
                                variant="contained"
                                sx={{
                                    backgroundColor: '#fff',
                                    color: '#e0e0e0',
                                    fontWeight: '500',
                                    borderRadius: '8px',
                                    border: '1px #e0e0e0 solid',
                                    textTransform: 'none',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    alignSelf: 'center',
                                    width: 'auto',
                                    padding: '6.5px 10px 6.5px 12px',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#fff',
                                        color: '#e0e0e0',
                                        boxShadow: 'none',
                                    },
                                    '& .MuiButton-startIcon': {
                                        marginRight: 0,
                                        marginLeft: 0,
                                    },
                                    '& .MuiButton-endIcon': {
                                        marginLeft: memberSubteams.length === 0 ? 0 : 1,
                                    },
                                }}
                                startIcon={renderSubteamButtonIcon()}
                                onClick={handleSubteamDropdownOpen}
                            />

                            {/* Subteam List Modal */}
                            <TeamSubteamListModal
                                isOpen={isSubteamModalOpen}
                                onClose={handleSubteamDropdownClose}
                                anchorEl={subteamButtonRef.current}
                                user={user}
                                teamId={cardDetails?.card?.teamId || currentTeamId}
                                onSubteamsUpdated={() => {
                                    // Refresh member subteams when updated
                                    const teamId = cardDetails?.card?.teamId || currentTeamId;
                                    if (teamId && user?.userId) {
                                        const fetchMemberSubteams = async () => {
                                            try {
                                                const response = await getTeamMembers(teamId, { status: 'active' });
                                                if (response && response.members) {
                                                    const member = response.members.find(m => {
                                                        const memberUserId = m.userId || m.user?.userId;
                                                        return memberUserId === user.userId;
                                                    });
                                                    if (member && member.subteamIds && Array.isArray(member.subteamIds)) {
                                                        const subteams = member.subteamIds.map(subteam => {
                                                            if (subteam && typeof subteam === 'object' && subteam._id) {
                                                                return {
                                                                    _id: subteam._id.toString(),
                                                                    name: subteam.name || 'Unnamed Subteam',
                                                                    logoUrl: subteam.logoUrl || '',
                                                                    description: subteam.description || ''
                                                                };
                                                            }
                                                            return {
                                                                _id: subteam.toString(),
                                                                name: 'Subteam',
                                                                logoUrl: '',
                                                                description: ''
                                                            };
                                                        });
                                                        setMemberSubteams(subteams);
                                                    } else {
                                                        setMemberSubteams([]);
                                                    }
                                                }
                                            } catch (error) {
                                                console.error('Error refreshing member subteams:', error);
                                            }
                                        };
                                        fetchMemberSubteams();
                                    }
                                }}
                            />
                        </Box>
                    </Grid>

                    <Grid item xs={12} sm="auto">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TeamDarkButtonWithIcon
                                text="Share Your Card"
                                icon={<ReplyOutlinedIcon />}
                                iconPosition="end"
                                onClick={handleShareCard}
                            />

                            <Button
                                variant="contained"
                                sx={{
                                    backgroundColor: '#fff',
                                    color: '#cccccc',
                                    fontWeight: '500',
                                    borderRadius: '12px',
                                    border: '1px #e0e0e0 solid',
                                    textTransform: 'none',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '75px',
                                    padding: '6.5px 16px',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#fff',
                                        color: '#828282',
                                        boxShadow: 'none',
                                    },
                                }}
                                onClick={handleMenuOpen}
                            >
                                <MoreHorizOutlinedIcon sx={{ color: '#828282' }} />
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Grid>


            <TeamUpgradeSnackBar
                open={snackbarOpen}
                handleClose={handleSnackbarClose}
                handleSubscribe={handleSubscribe}
            />

            {/* Share Card Modal */}
            <TeamShareYourCardModal
                open={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                cardId={cardId} // Pass the specific card ID
            />

            {/* Three dots menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        minWidth: '200px',
                        marginTop: '12px'
                    }
                }}
            >
                <MenuItem onClick={handleMemberSettings}>
                    <ListItemIcon>
                        <SettingsOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Member Settings" />
                </MenuItem>
                <MenuItem onClick={handleDuplicateCard}>
                    <ListItemIcon>
                        <ContentCopyOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Duplicate Card" />
                </MenuItem>
                <MenuItem onClick={handleShareCard}>
                    <ListItemIcon>
                        <ShareOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Share Card" />
                </MenuItem>
                <MenuItem onClick={handleDownloadQRCode}>
                    <ListItemIcon>
                        <QrCodeOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Download QR Code" />
                </MenuItem>
                {!isCompanyPage && (
                    <MenuItem onClick={handleResendInvite}>
                        <ListItemIcon>
                            <EmailOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Resend Invite" />
                    </MenuItem>
                )}
                {!isCompanyPage && (
                    <MenuItem onClick={handleResetPassword}>
                        <ListItemIcon>
                            <LockResetOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Reset Password" />
                    </MenuItem>
                )}
                <Divider />
                <MenuItem
                    onClick={handleDeleteCardClick}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon>
                        <DeleteOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    <ListItemText primary="Delete Card" />
                </MenuItem>
                <MenuItem
                    onClick={handleRemoveMember}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon>
                        <PersonOutlineOutlinedIcon fontSize="small" sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    <ListItemText primary="Remove Member" />
                </MenuItem>
                <MenuItem
                    onClick={handleDeleteMember}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon>
                        <DeleteOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    <ListItemText primary="Delete Member" />
                </MenuItem>
            </Menu>

            {/* Notification Snackbar */}
            <TeamNotificationSnackbar
                open={notification.open}
                message={notification.message}
                handleClose={handleNotificationClose}
                severity={notification.severity}
            />

            {/* Delete Card Modal */}

            <TeamDeleteCardModal
                open={isDeleteCardModalOpen}
                onClose={handleDeleteCardCancel}
                onConfirm={handleDeleteCardConfirm}
            />

            {/* Member Settings Modal */}
            <TeamMemberSettingsModal
                open={isMemberSettingsModalOpen}
                onClose={handleMemberSettingsModalClose}
                memberData={user}
            />

            {/* Duplicate Member Modal */}
            <TeamDuplicateMemberModal
                open={isDuplicateMemberModalOpen}
                onClose={handleDuplicateMemberModalClose}
                memberData={{
                    userId: user?.userId,
                    teamId: cardDetails?.card?.teamId || user?.teamId
                }}
            />

            {/* Remove Member Modal */}
            <TeamRemoveMemberModal
                open={isRemoveMemberModalOpen}
                onClose={handleRemoveMemberClose}
                onConfirm={handleRemoveMemberConfirm}
            />

            {/* Delete Member Modal */}
            <TeamDeleteMemberModal
                open={isDeleteMemberModalOpen}
                onClose={handleDeleteMemberClose}
                onConfirm={handleDeleteMemberConfirm}
            />

        </Box>
    );
};

export default TeamEditMemberNavbar;